/**
 * ResilienceTrendCard -- Home tile, 30-day R/A/G stacked-area
 * (P2.9.c).
 *
 * Hand-rolled SVG, no chart-lib dep. Layout: title + SVG area +
 * legend with current-day band counts. Three states:
 *   - loading: small placeholder
 *   - error  : silent (returns null) so Home doesn't shout at the
 *              operator on every transient 4xx/5xx tick
 *   - success: SVG bands + legend
 *
 * One-off implementation per v0.2.0 plan (Q4 decision). Backlog
 * #443 covers extraction to a generic StackedAreaChart when a
 * 2nd surface needs the same visual.
 */
import { useTranslation } from "react-i18next";

import { useResilienceTrend } from "twin/api/hooks";
import type { ResilienceTrendPoint } from "shared/api/types";

// P214.6 (Observatory): muted chart-band fills (tailwind chart.* tokens)
// so the stacked area reads as calm daylight data, not saturated RAG.
const BAND_COLOURS = {
  red: "#DC9385", // chart.redBand
  amber: "#E8C36A", // chart.amberBand
  green: "#9CC9AB", // chart.greenBand
  unknown: "#CBD5E1", // chart.unknown
} as const;

const LEGEND_TEXT = {
  red: "text-ink-2",
  amber: "text-ink-2",
  green: "text-ink-2",
  unknown: "text-ink-muted",
} as const;

const SVG_WIDTH = 600;
const SVG_HEIGHT = 160;
const PAD_L = 28;
const PAD_R = 8;
const PAD_T = 8;
const PAD_B = 24;

const BAND_ORDER = ["red", "amber", "green", "unknown"] as const;
type BandKey = (typeof BAND_ORDER)[number];

interface StackedSeries {
  /** For each band, an array of per-day cumulative top-y values. */
  cumulative: Record<BandKey, number[]>;
  maxTotal: number;
  dayCount: number;
}

function buildSeries(points: ResilienceTrendPoint[]): StackedSeries {
  const dayCount = points.length;
  const cumulative: Record<BandKey, number[]> = {
    red: [],
    amber: [],
    green: [],
    unknown: [],
  };
  let maxTotal = 0;

  for (const point of points) {
    const c = point.counts;
    const total = c.red + c.amber + c.green + c.unknown;
    if (total > maxTotal) maxTotal = total;
    // Cumulative from bottom: unknown -> green -> amber -> red so the
    // R/A/G bands stack visually with red on top (most severe).
    const u = c.unknown;
    const g = u + c.green;
    const a = g + c.amber;
    const r = a + c.red;
    cumulative.unknown.push(u);
    cumulative.green.push(g);
    cumulative.amber.push(a);
    cumulative.red.push(r);
  }
  // Avoid divide-by-zero when every day has 0 entities.
  if (maxTotal === 0) maxTotal = 1;
  return { cumulative, maxTotal, dayCount };
}

function bandPath(
  cumulativeAbove: number[],
  cumulativeBelow: number[] | null,
  series: StackedSeries,
): string {
  const innerW = SVG_WIDTH - PAD_L - PAD_R;
  const innerH = SVG_HEIGHT - PAD_T - PAD_B;
  const xStep = series.dayCount > 1 ? innerW / (series.dayCount - 1) : innerW / 2;

  const yFor = (val: number): number =>
    PAD_T + innerH - (val / series.maxTotal) * innerH;

  // Top edge: left -> right along cumulativeAbove.
  const topPoints = cumulativeAbove
    .map((v, i) => `${PAD_L + i * xStep},${yFor(v)}`)
    .join(" L ");

  // Bottom edge: right -> left. If cumulativeBelow is null (first
  // band from the bottom), close along the baseline y0.
  let bottomPoints: string;
  if (cumulativeBelow === null) {
    const y0 = yFor(0);
    bottomPoints = `${PAD_L + (series.dayCount - 1) * xStep},${y0} L ${PAD_L},${y0}`;
  } else {
    bottomPoints = cumulativeBelow
      .map((v, i) => {
        const idx = series.dayCount - 1 - i;
        return `${PAD_L + idx * xStep},${yFor(v)}`;
      })
      .reverse()
      .reverse() // double reverse to keep readability; same result as one map
      .join(" L ");
    // Build by walking right-to-left.
    const rtl: string[] = [];
    for (let i = series.dayCount - 1; i >= 0; i--) {
      rtl.push(`${PAD_L + i * xStep},${yFor(cumulativeBelow[i] ?? 0)}`);
    }
    bottomPoints = rtl.join(" L ");
  }

  return `M ${topPoints} L ${bottomPoints} Z`;
}

export function ResilienceTrendCard() {
  const { t } = useTranslation();
  const trend = useResilienceTrend({ window: 30 });

  if (trend.isError) {
    return null;
  }

  if (trend.isLoading || !trend.data) {
    return (
      <section
        data-testid="resilience-trend-card-loading"
        className="rounded-xl border border-paper-2 bg-white px-[26px] py-6 shadow-card text-sm text-ink-muted h-full flex flex-col"
      >
        {t("modules.twin.resilienceTrend.loading")}
      </section>
    );
  }

  const series = buildSeries(trend.data.points);
  const todayCounts = trend.data.points[trend.data.points.length - 1]?.counts ?? {
    red: 0,
    amber: 0,
    green: 0,
    unknown: 0,
  };
  const totalToday =
    todayCounts.red + todayCounts.amber + todayCounts.green + todayCounts.unknown;

  if (totalToday === 0 && series.maxTotal <= 1) {
    return (
      <section
        data-testid="resilience-trend-card-empty"
        className="rounded-xl border border-paper-2 bg-white px-[26px] py-6 shadow-card h-full flex flex-col"
      >
        <h2 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted mb-2">
          {t("modules.twin.resilienceTrend.heading")}
        </h2>
        <p className="text-xs text-ink-muted">
          {t("modules.twin.resilienceTrend.emptyTitle")} --{" "}
          {t("modules.twin.resilienceTrend.emptyBody")}
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid="resilience-trend-card"
      className="rounded-xl border border-paper-2 bg-white px-[26px] py-6 shadow-card h-full flex flex-col"
    >
      <header className="mb-3">
        <h2 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted">
          {t("modules.twin.resilienceTrend.heading")}
        </h2>
      </header>
      <svg
        data-testid="resilience-trend-svg"
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        preserveAspectRatio="none"
        className="w-full h-40"
        role="img"
        aria-label={t("modules.twin.resilienceTrend.heading")}
      >
        {/* Bottom-to-top band rendering. cumulativeBelow is the
            previous band's top edge (null for the bottom band). */}
        {([...BAND_ORDER].reverse() as BandKey[]).map(
          (band: BandKey, idx: number, arr: BandKey[]) => {
            const above = series.cumulative[band];
            const prev = arr[idx - 1];
            const below =
              idx === 0 || prev === undefined ? null : series.cumulative[prev];
            return (
              <path
                key={band}
                data-testid={`resilience-trend-band-${band}`}
                d={bandPath(above, below, series)}
                fill={BAND_COLOURS[band]}
                opacity={0.85}
              />
            );
          },
        )}
      </svg>
      <ul
        data-testid="resilience-trend-legend"
        className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs"
      >
        {BAND_ORDER.map((band) => (
          <li
            key={band}
            data-testid={`resilience-trend-legend-${band}`}
            className={`flex items-center gap-1.5 ${LEGEND_TEXT[band]}`}
          >
            <span
              className="inline-block w-3 h-3 rounded"
              style={{ backgroundColor: BAND_COLOURS[band] }}
            />
            <span className="font-medium">
              {t(`modules.twin.resilienceTrend.legend.${band}`)}
            </span>
            <span className="font-mono tabular-nums text-ink">{todayCounts[band]}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
